/**
 * The KPI grid's "Empty rows: Show | Hide" preference, remembered per browser
 * (DOP-293, DOP-609).
 *
 * SHOW by default since DOP-609. It started as hide-by-default so the grid a
 * user already knew stayed as it was; the Summit call showed the cost — an
 * analyst reading a grid with rows silently missing, behind a control whose
 * state they could not read. Shown, every blank row says why it is blank, and
 * hiding is a choice made in plain sight. A choice already stored (either
 * way) is kept.
 *
 * `localStorage` and not the server: this is a per-person view preference, not
 * a fact about the account, and the KPI surface is already per-user private.
 * Every access is guarded — a browser in private mode or with site data blocked
 * throws on the accessor itself, and a dashboard that cannot remember a toggle
 * is fine while one that crashes is not.
 */

import { useCallback, useEffect, useState } from "react";

export const HIDE_EMPTY_ROWS_STORAGE_KEY = "doppler.kpi.hideEmptyRows";

/** The stored answer, or null when nothing is stored (or storage is unusable). */
function readStored(): boolean | null {
	if (typeof window === "undefined") return null;
	try {
		const raw = window.localStorage.getItem(HIDE_EMPTY_ROWS_STORAGE_KEY);
		if (raw === null) return null;
		return raw === "true";
	} catch {
		return null;
	}
}

export interface HideEmptyRowsState {
	hideEmptyRows: boolean;
	toggleHideEmptyRows: () => void;
	setHideEmptyRows: (hide: boolean) => void;
}

export function useHideEmptyRows(): HideEmptyRowsState {
	// Read during the initial state computation rather than in an effect: an
	// effect would render the default grid first and then re-filter, which for a
	// user who chose "show everything" is a visible flash of the view they
	// turned off.
	const [hideEmptyRows, setHideEmptyRows] = useState<boolean>(
		() => readStored() ?? false,
	);

	useEffect(() => {
		if (typeof window === "undefined") return;
		try {
			window.localStorage.setItem(
				HIDE_EMPTY_ROWS_STORAGE_KEY,
				String(hideEmptyRows),
			);
		} catch {
			// See `readStored`: unusable storage must not break the toggle.
		}
	}, [hideEmptyRows]);

	const toggleHideEmptyRows = useCallback(
		() => setHideEmptyRows((current) => !current),
		[],
	);

	return { hideEmptyRows, toggleHideEmptyRows, setHideEmptyRows };
}
